import { MODE_VARIANTS, PALETTE_VARIANTS } from "@/lib/theme";
import { useUserPreferences } from "@/hooks/useUserPreferences";

import { OptionTile } from "./OptionTile";
import { PreferenceCard } from "./PreferenceCard";
import { ThemePreview } from "./ThemePreview";

/**
 * Preferences → Appearance: light or dark, and the palette. Each choice
 * previews as a miniature of the app in that look — the mode tiles in the
 * current palette, the palette tiles in the current mode — so the pair reads
 * as one decision.
 */
export function AppearanceSection() {
  const { effective, save, readOnly } = useUserPreferences();

  return (
    <div className="flex flex-col gap-4">
      <PreferenceCard title="Mode" description="The overall tone of the interface.">
        <div role="radiogroup" aria-label="Mode" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {MODE_VARIANTS.map((v) => {
            const Icon = v.icon;
            return (
              <OptionTile
                key={v.id}
                selected={effective.theme_mode === v.id}
                disabled={readOnly}
                onSelect={() => save({ theme_mode: v.id })}
                preview={<ThemePreview mode={v.id} palette={effective.theme_palette} />}
                label={
                  <span className="flex items-center gap-1.5">
                    <Icon size={14} strokeWidth={1.75} className="text-text-dim" />
                    {v.label}
                  </span>
                }
              />
            );
          })}
        </div>
      </PreferenceCard>

      <PreferenceCard
        title="Palette"
        description="Recolors the chrome — backgrounds, surfaces and accents. Status colors and charts keep their meaning in every palette."
      >
        <div
          role="radiogroup"
          aria-label="Palette"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {PALETTE_VARIANTS.map((v) => (
            <OptionTile
              key={v.id}
              selected={effective.theme_palette === v.id}
              disabled={readOnly}
              onSelect={() => save({ theme_palette: v.id })}
              preview={<ThemePreview mode={effective.theme_mode} palette={v.id} />}
              label={v.label}
              description={v.description}
            />
          ))}
        </div>
      </PreferenceCard>
    </div>
  );
}
